import { t } from 'i18next'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { Link, useLocation, useHistory } from 'react-router-dom'

import {
  StyledBurgerButton,
  StyledClinNavBar,
  IStyledClinNavBarProps,
  StyledNavItemsContainer,
  StyledNavIconsContainer,
  StyledLogo,
  StyledLogoLink,
  StyledLogoLinkInner,
  StyledSecondNavWrapper,
  StyledHeaderWrapper,
  StyledBackButton,
  StyledLanguageButton,
  StyledLanguageOption,
  StyledMobileLogo
} from './ClinNavbar.styles'
import EarthIcon from '../../assets/media/icons/earth.svg'
import { ClinTheme } from '../../ClinTheme'
import { isAusGaUser, UserRoleRecord, UserRole } from '../../constants'
import { useAppContext } from '../../context/app'
import { AutoSuggest } from '../../features/Search/AutoSuggest'
import {
  GlobalSearch,
  GlobalSearchOld
} from '../../features/Search/GlobalSearch'
import { useCheckUnableHomePage } from '../../hooks/useCheckUnableHomePage/useCheckUnableHomePage'
import i18n, { defaultLanguages } from '../../i18n/config'
import ColumnTooltip from '../../pages/OrderPages/Orders/ColumnTooltip/ColumnTooltip'
import { INavigationItem } from '../../services/ContentProvider'
import { CatalogDtoSuggestionDto } from '../../types/swaggerTypes'
import { ClinIcon } from '../ClinIcon'
import { ClinIconPathName } from '../ClinIcon/ClinIcon.paths'
import { ClinNavItem } from '../ClinNavItem'
import { ClinText } from '../ClinText'
import { AutoSuggestOld } from '../../features/Search/AutoSuggest/old/AutoSuggestOld'
import { FeatureFlagKeys } from '../../context/featureFlags/FeatureFlagKeys'
import { useFeatureFlags } from '../../context/featureFlags/FeatureFlagContext'

interface IClinNavbarProps extends IStyledClinNavBarProps {
  /** Nav items  */
  navItems?:
    | React.ReactElement<typeof ClinNavItem>[]
    | React.ReactElement<typeof ClinNavItem>
  /**Child Nav items  */
  childNavItems: INavigationItem[] | undefined
  /** Icon nav items  */
  navIcons?: React.ReactNode
  /** Whether the navbar is open initially */
  isMenuOpen?: boolean
  /** Call back to inform when a nav menu is closed */
  resetOnClose: () => void
  resetFilteredChildNavItems: () => void
}

export const ClinNavbar: FunctionComponent<IClinNavbarProps> = ({
  navItems,
  childNavItems,
  navIcons,
  isMenuOpen = false,
  resetOnClose,
  resetFilteredChildNavItems
}) => {
  const { userRole, portfolioCountryCode } = useAppContext()
  const [isBurgerOpen, setIsBurgerOpen] = React.useState<boolean>(isMenuOpen)
  const { enableNewHomePage } = useCheckUnableHomePage()
  const history = useHistory()

  const [isMobile, setIsMobile] = React.useState<boolean>(false)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState<boolean>(false)
  const [recentSearches, setRecentSearches] = useState<
    CatalogDtoSuggestionDto[]
  >([])
  const [isAliasesDisplayed, setIsAliasesDisplayed] = useState<boolean>(false)
  const { useFeatureFlag } = useFeatureFlags()
  const enableNewGlobalSearch = useFeatureFlag(FeatureFlagKeys.NewGlobalSearch)
  // https://clinigen.atlassian.net/browse/CLOS-8642

  const baseUrl =
    isAusGaUser(portfolioCountryCode, userRole) || enableNewHomePage
      ? '/home'
      : '/orders'

  // Custom search handler for header search functionality
  const handleHeaderSearch = (query: string) => {
    const isMaUser = !!(
      userRole && UserRoleRecord[userRole as UserRole].isMaUser
    )
    // Construct URL with search query and default pagination parameters
    const userRolePath = isMaUser ? `/results` : `/products/catalogue`
    const searchUrl = `${userRolePath}?q=${encodeURIComponent(
      query
    )}&pageIndex=0&pageSize=10`
    history.push(searchUrl)
  }

  const updateIsMobile = () => {
    setIsMobile(
      (window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth) <= ClinTheme.breakpoints[2]
    )
  }

  const listener = () => {
    updateIsMobile()
    isBurgerOpen && !isMobile && setIsBurgerOpen(false)
  }

  useEffect(() => {
    updateIsMobile() // Set initial state
    window.addEventListener('resize', listener)
    return () => {
      window.removeEventListener('resize', listener)
    }
  }, [isBurgerOpen])

  // Load recent searches from localStorage
  useEffect(() => {
    const loadRecentSearches = () => {
      const storedSearches = JSON.parse(
        localStorage.getItem('recentSearches') || '[]'
      )

      // Filter to only include valid CatalogDtoSuggestionDto objects
      // This provides backward compatibility with old storage format
      const validRecentSearches = storedSearches.filter((search: any) => {
        return (
          search.document &&
          (search.document.catalogDocumentId || search.document.programId)
        )
      })

      setRecentSearches(
        validRecentSearches.length > 0 ? validRecentSearches : []
      )
    }

    // Load initial recent searches
    loadRecentSearches()

    // Listen for storage changes to keep recent searches in sync across components
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'recentSearches') {
        loadRecentSearches()
      }
    }

    // Listen for custom event to sync recent searches within the same tab
    const handleRecentSearchesUpdate = () => {
      loadRecentSearches()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('recentSearchesUpdated', handleRecentSearchesUpdate)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(
        'recentSearchesUpdated',
        handleRecentSearchesUpdate
      )
    }
  }, [])

  // Close menu if we navigate away
  const location = useLocation()
  useEffect(() => {
    setIsBurgerOpen(false)
    setIsLanguageMenuOpen(false)
  }, [location])

  // Check if we're on the homepage
  const isHomePage =
    location.pathname === '/home' || location.pathname === '/homepage'

  // If open on mobile hide overflow to stop scrolling
  useEffect(() => {
    document.body.style.overflow = isBurgerOpen ? 'hidden' : 'auto'
  }, [isBurgerOpen])

  const onClickLanguage = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen)
  }

  const childNavItemsList =
    childNavItems &&
    childNavItems?.map((nav) => (
      <Link
        key={nav.path}
        to={nav.path}
        onClick={(e) => {
          e.stopPropagation()
          resetFilteredChildNavItems()
        }}
      >
        <StyledLanguageOption>{nav.title}</StyledLanguageOption>
      </Link>
    ))

  return (
    <>
      <StyledClinNavBar className={isHomePage ? 'homepage-navbar' : ''}>
        <StyledBurgerButton
          className="nav-burger"
          onClick={() => {
            setIsBurgerOpen(!isBurgerOpen)
            setIsLanguageMenuOpen(false)
            isBurgerOpen && resetOnClose()
            resetFilteredChildNavItems()
          }}
        >
          {(!isBurgerOpen || !childNavItemsList) && !isLanguageMenuOpen && (
            <ClinIcon
              iconName={
                isBurgerOpen
                  ? ClinIconPathName.BurgerClosed
                  : ClinIconPathName.Burger
              }
              iconSize={ClinTheme.space[5]}
              iconFill={ClinTheme.colors.white}
            />
          )}
        </StyledBurgerButton>
        {!isBurgerOpen && (
          <StyledHeaderWrapper>
            <StyledLogoLink to={baseUrl}>
              <StyledLogoLinkInner>
                <ColumnTooltip tooltipText={t('navigation:clinigen_logo')}>
                  <StyledLogo inverted={true} />
                  <StyledMobileLogo />
                </ColumnTooltip>
              </StyledLogoLinkInner>
            </StyledLogoLink>
            {enableNewGlobalSearch ? (
              <AutoSuggest
                pathsToHide={['/home', '/homepage']}
                noResultsText={t('global_search:no_results_text')}
                maUserNoResultsText={t('global_search:ma_user_no_results_text')}
                suggestionText={t('global_search:suggestion_text')}
                noResultsPath={'/products/catalogue?q=*'}
                showIcons={true}
                recentSearches={recentSearches}
                showRecentSearches={false}
                showNoResults={false}
                isHeader={true}
                isAliasesDisplayed={isAliasesDisplayed}
                setIsAliasesDisplayed={setIsAliasesDisplayed}
                handleOnSearch={handleHeaderSearch}
                searchComponent={(props) => (
                  <GlobalSearch
                    {...props}
                    isAusGa={isAusGaUser(portfolioCountryCode, userRole)}
                    isMenuSearch={false}
                  />
                )}
                isShowExactMatch={false}
              />
            ) : (
              <AutoSuggestOld
                pathsToHide={['/home', '/homepage']}
                noResultsText={t('global_search:no_results_text')}
                maUserNoResultsText={t('global_search:ma_user_no_results_text')}
                suggestionText={t('global_search:suggestion_text')}
                noResultsPath={'/products/catalogue?q=*'}
                showNoResults={false}
                recentSearches={recentSearches}
                showRecentSearches={false}
                handleOnSearch={handleHeaderSearch}
                searchComponent={(props) => (
                  <GlobalSearch
                    {...props}
                    isAusGa={isAusGaUser(portfolioCountryCode, userRole)}
                    isMenuSearch={false}
                  />
                )}
              />
            )}
            {!isMobile && (
              <StyledNavIconsContainer className="nav-icons-container">
                {navIcons}
              </StyledNavIconsContainer>
            )}
          </StyledHeaderWrapper>
        )}
      </StyledClinNavBar>
      <StyledSecondNavWrapper>
        {isBurgerOpen &&
          (enableNewGlobalSearch ? (
            <AutoSuggest
              noResultsText={t('global_search:no_results_text')}
              maUserNoResultsText={t('global_search:ma_user_no_results_text')}
              suggestionText={t('global_search:suggestion_text')}
              noResultsPath={'/products/catalogue?q=*'}
              showIcons={true}
              recentSearches={recentSearches}
              showNoResults={false}
              isHeader={true}
              isAliasesDisplayed={isAliasesDisplayed}
              setIsAliasesDisplayed={setIsAliasesDisplayed}
              handleOnSearch={handleHeaderSearch}
              searchComponent={(props) => (
                <GlobalSearch
                  {...props}
                  isAusGa={isAusGaUser(portfolioCountryCode, userRole)}
                  isMenuSearch={true}
                />
              )}
              isShowExactMatch={false}
            />
          ) : (
            <AutoSuggestOld
              noResultsText={t('global_search:no_results_text')}
              maUserNoResultsText={t('global_search:ma_user_no_results_text')}
              suggestionText={t('global_search:suggestion_text')}
              noResultsPath={'/products/catalogue?q=*'}
              showNoResults={false}
              handleOnSearch={handleHeaderSearch}
              searchComponent={(props) => (
                <GlobalSearchOld
                  {...props}
                  isAusGa={isAusGaUser(portfolioCountryCode, userRole)}
                  isMenuSearch={true}
                />
              )}
            />
          ))}
        {!isLanguageMenuOpen ? (
          <StyledNavItemsContainer
            isMenuOpen={isBurgerOpen}
            className="nav-items-container"
            style={{ paddingTop: '0' }}
          >
            {isMobile && childNavItemsList && (
              <StyledBackButton
                className="nav-back"
                onClick={() => {
                  resetFilteredChildNavItems()
                }}
              >
                <ClinIcon
                  iconName={ClinIconPathName.ChevronLeft}
                  iconSize={ClinTheme.space[5]}
                  iconFill={ClinTheme.colors.midGrey}
                />
                <ClinText
                  color={ClinTheme.colors.midGrey}
                  fontWeight={ClinTheme.fontWeights.medium}
                >
                  {t('common:buttons.back_button_text')}
                </ClinText>
              </StyledBackButton>
            )}
            {(isMobile && childNavItemsList) || navItems}
            {isMobile && !childNavItemsList && (
              <StyledLanguageButton
                onClick={(e) => {
                  e.stopPropagation()
                  onClickLanguage()
                }}
              >
                <img src={EarthIcon} alt="Earth Icon" className="earth-icon" />
                {defaultLanguages.find((l) => l.id === i18n.language)?.value}
                <ClinIcon
                  iconName={ClinIconPathName.ChevronRight}
                  iconSize={ClinTheme.space[5]}
                  iconFill={ClinTheme.colors.midGrey}
                />
              </StyledLanguageButton>
            )}
          </StyledNavItemsContainer>
        ) : (
          <StyledNavItemsContainer
            isMenuOpen={isBurgerOpen && isLanguageMenuOpen}
          >
            <StyledBackButton
              className="nav-back"
              onClick={() => {
                setIsLanguageMenuOpen(!isLanguageMenuOpen)
                isLanguageMenuOpen && resetOnClose()
              }}
            >
              <ClinIcon
                iconName={ClinIconPathName.ChevronLeft}
                iconSize={ClinTheme.space[5]}
                iconFill={ClinTheme.colors.midGrey}
              />
              <ClinText
                color={ClinTheme.colors.midGrey}
                fontWeight={ClinTheme.fontWeights.medium}
              >
                {t('common:buttons.back_button_text')}
              </ClinText>
            </StyledBackButton>

            {defaultLanguages
              .filter((language) => language.id !== 'cimode')
              .map((language) => (
                <StyledLanguageOption
                  key={language.id}
                  onClick={() => {
                    i18n.changeLanguage(language.id)
                    setIsLanguageMenuOpen(false)
                  }}
                >
                  {language.value}
                </StyledLanguageOption>
              ))}
          </StyledNavItemsContainer>
        )}
      </StyledSecondNavWrapper>
    </>
  )
}
